/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-26
 */

// Add third-party dependencies.
import { Box, IconButton, useMediaQuery, useTheme } from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Business as StudioIcon,
  Support as SupportIcon,
  Build as ToolsIcon,
  AttachMoney as PricingIcon,
  Menu as MenuIcon,
  // Queue as WaitlistIcon, // Commented out for MVP launch - keep for potential rollback
  Login as LoginIcon,
  PersonAdd as SignUpIcon,
} from '@mui/icons-material';
import { useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

// Add local dependencies.
import NavigationItem from '../common/NavigationItem';
import MobileMenu from './MobileMenu';
import { useSessionAuth } from '../../../hooks/useSessionAuth';
import {
  selectMobileMenuOpen,
  toggleMobileMenu,
} from '../../../features/navigation';

/**
 * Props for the MainNavigation component.
 */
interface MainNavigationProps {
  /**
   * Additional CSS classes.
   */
  className?: string;
  /**
   * Whether to show in mobile layout.
   * @default false
   */
  isMobile?: boolean;
}

/**
 * Navigation item data structure.
 */
interface NavigationItemData {
  label: string;
  path: string;
  icon?: React.ReactNode;
  requiresAuth?: boolean;
  comingSoon?: boolean;
}

/**
 * Main navigation component that provides primary navigation items.
 * Handles authentication-based visibility and active states.
 *
 * @param props - The component props
 * @returns {React.ReactElement} The main navigation component
 *
 * @example
 * return (
 *   <MainNavigation />
 * );
 *
 * @example
 * // Mobile layout
 * return (
 *   <MainNavigation isMobile={true} />
 * );
 */
const MainNavigation: React.FC<MainNavigationProps> = ({
  className,
  isMobile = false,
}) => {
  const theme = useTheme();
  const { isAuthenticated } = useSessionAuth();
  const location = useLocation();
  const dispatch = useDispatch();
  const { t } = useTranslation('navigation');

  // Get navigation state from Redux
  const isMobileMenuOpen = useSelector(selectMobileMenuOpen);

  // Detect mobile breakpoint (< 768px)
  const isMobileBreakpoint = useMediaQuery(theme.breakpoints.down('md'));
  const shouldShowMobile = isMobile || isMobileBreakpoint;

  // Define navigation items
  const navigationItems: NavigationItemData[] = [
    // Protected items (only show when authenticated)
    {
      label: t('dashboard'),
      path: '/dashboard',
      icon: <DashboardIcon fontSize="small" />,
      requiresAuth: true,
    },
    {
      label: t('studios'),
      path: '/studios',
      icon: <StudioIcon fontSize="small" />,
      requiresAuth: true,
    },
    {
      label: t('tools'),
      path: '/tools',
      icon: <ToolsIcon fontSize="small" />,
      requiresAuth: true,
    },
    {
      label: t('pricing'),
      path: '/pricing',
      icon: <PricingIcon fontSize="small" />,
      requiresAuth: false,
    },
    // Public items (show based on auth state)
    // Waitlist item commented out for MVP launch - keep for potential rollback
    // {
    //   label: t('waitlist'),
    //   path: '/waitlist',
    //   icon: <WaitlistIcon fontSize="small" />,
    //   requiresAuth: false,
    // },
    {
      label: t('signUp'),
      path: '/register',
      icon: <SignUpIcon fontSize="small" />,
      requiresAuth: false,
    },
    {
      label: t('signIn'),
      path: '/login',
      icon: <LoginIcon fontSize="small" />,
      requiresAuth: false,
    },
    {
      label: t('support'),
      path: '/contact',
      icon: <SupportIcon fontSize="small" />,
      requiresAuth: false,
    },
  ];

  // Filter items based on authentication status
  const visibleItems = navigationItems.filter(item => {
    // Hide protected items when not authenticated
    if (item.requiresAuth && !isAuthenticated) {
      return false;
    }
    // Hide auth-related items when authenticated (except Support and Pricing which are always visible)
    if (
      isAuthenticated && // item.path === '/waitlist' || // Commented out for MVP launch - keep for potential rollback
      (item.path === '/login' || item.path === '/register')
    ) {
      return false;
    }
    return true;
  });

  /**
   * Handle mobile menu toggle.
   */
  const handleMobileMenuToggle = () => {
    dispatch(toggleMobileMenu());
  };

  // Show mobile hamburger menu on small screens
  if (shouldShowMobile) {
    return (
      <>
        <Box
          className={className}
          sx={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <IconButton
            onClick={handleMobileMenuToggle}
            sx={{
              minWidth: 44,
              minHeight: 44,
              color: 'inherit',
            }}
            aria-label="Open navigation menu"
          >
            <MenuIcon />
          </IconButton>
        </Box>

        {/* Mobile Menu Drawer */}
        <MobileMenu
          isOpen={isMobileMenuOpen}
          onClose={() => dispatch(toggleMobileMenu())}
        />
      </>
    );
  }

  // Desktop navigation
  return (
    <Box
      className={className}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
      }}
    >
      {/* Navigation items */}
      {visibleItems.map(item => (
        <NavigationItem
          key={item.path}
          label={item.label}
          path={item.path}
          icon={item.icon}
          isActive={
            item.path === '/studios'
              ? location.pathname.startsWith('/studio') ||
                location.pathname === '/studios'
              : location.pathname === item.path
          }
        />
      ))}
    </Box>
  );
};

export default MainNavigation;
